import type { KdsTicket, KdsOrderItem, AggregatedDish, AggregatedEntry } from '../types';

/**
 * Generate aggregation key: product_id only — details shown per-entry inside the card.
 *
 * 临时菜品立宪 2026-08-01:临时行没有 `product_id`(`null`),不能落进同一个
 * `"null"` 桶 —— 那会把两道**不同的**临时菜聚合成一张卡,厨师看到"海鲜拼盘 ×2"
 * 而实际是一道海鲜拼盘 + 一道别的。临时行按 `instance_id` 各自成组:它们本来
 * 就不该跨单合并(立宪 §2-b.3 "临时菜不合并是正确行为")。
 */
export function aggregationKey(item: KdsOrderItem): string {
  return item.product_id == null ? `ad_hoc:${item.instance_id}` : `catalog:${item.product_id}`;
}

/**
 * Read an item's call-number (`external_id`) defensively. This runs inside the
 * Chef-view render path (useMemo), so a single malformed KDS payload must NOT
 * throw — that would blow the whole kitchen screen up through the ErrorBoundary.
 * Missing / non-numeric values warn and degrade to `null`.
 */
function readItemExternalId(item: KdsOrderItem): number | null {
  const externalId = (item as { external_id?: unknown }).external_id;
  if (typeof externalId !== 'number' || !Number.isFinite(externalId)) {
    console.warn(
      `[kds] item ${item.id} (product_id=${item.product_id}) has a missing or non-numeric external_id; `
      + 'ignoring it for call-number aggregation',
    );
    return null;
  }
  return externalId;
}

/** Aggregate KDS tickets into dish cards for chef view (cross-ticket, by product) */
export function aggregateForChef(tickets: KdsTicket[]): AggregatedDish[] {
  // Flatten all active items across all tickets
  const allItems: { item: KdsOrderItem; ticket: KdsTicket }[] = [];
  for (const ticket of tickets) {
    for (const item of ticket.items) {
      if (item.status === 'InProgress' || item.status === 'Pending') {
        allItems.push({ item, ticket });
      }
    }
  }

  const groups = new Map<string, { item: KdsOrderItem; ticket: KdsTicket }[]>();
  for (const entry of allItems) {
    const key = aggregationKey(entry.item);
    const list = groups.get(key) ?? [];
    list.push(entry);
    groups.set(key, list);
  }

  const dishes: AggregatedDish[] = [];
  for (const [key, groupEntries] of groups) {
    groupEntries.sort((a, b) => a.item.created_at - b.item.created_at);

    const entries: AggregatedEntry[] = [];
    let externalId: number | null = null;
    for (const { item, ticket } of groupEntries) {
      const itemExternalId = readItemExternalId(item);
      if (itemExternalId != null) {
        if (externalId == null) {
          externalId = itemExternalId;
        } else if (externalId !== itemExternalId) {
          console.warn(
            `[kds] aggregation ${key} (product_id=${item.product_id}) has inconsistent external_id: `
            + `keeping ${externalId}, ignoring ${itemExternalId}`,
          );
        }
      }
      const remaining = item.total_quantity - item.done_quantity;
      if (remaining <= 0) continue;
      entries.push({
        order_id: item.order_id,
        item_id: item.id,
        table_name: ticket.table_name ?? null,
        receipt_number: ticket.receipt_number,
        remaining,
        created_at: item.created_at,
        started_at: item.started_at ?? null,
        options: item.options,
        spec_name: item.spec_name ?? null,
        note: item.note ?? null,
        guest_allergens: item.guest_allergens ?? [],
        product_allergens: item.product_allergens ?? [],
      });
    }

    const total_remaining = entries.reduce((sum, e) => sum + e.remaining, 0);
    if (total_remaining <= 0) continue;

    const first = groupEntries[0].item;
    let resolvedExternalId = externalId;
    if (resolvedExternalId == null) {
      console.warn(
        `[kds] aggregation ${key} (product_id=${first.product_id}) has remaining items but no numeric `
        + 'external_id; falling back to 0 so the dish still renders',
      );
      resolvedExternalId = 0;
    }

    dishes.push({
      key,
      product_id: first.product_id,
      kitchen_name: first.kitchen_name,
      external_id: resolvedExternalId,
      total_remaining,
      entries,
      items: groupEntries.map((e) => e.item),
    });
  }

  dishes.sort((a, b) => {
    const aOldest = a.entries[0]?.created_at ?? Infinity;
    const bOldest = b.entries[0]?.created_at ?? Infinity;
    return aOldest - bOldest;
  });
  return dishes;
}

/** Find the FIFO target item for +1 done */
export function findFifoTarget(dish: AggregatedDish): KdsOrderItem | null {
  if (dish.entries.length === 0) return null;
  const oldest = dish.entries[0];
  return (
    dish.items.find(
      (i) =>
        i.id === oldest.item_id &&
        i.done_quantity < i.total_quantity,
    ) ?? null
  );
}
